import React, { useCallback, useRef, useState } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import InputValidationTooltip from "../../common/InputValidationTooltip";
import { nonDigitGlobalRegex } from "constants/validations";
import { useNavigate } from "react-router-dom";

interface ZipCodeFieldProps {
    register: UseFormRegisterReturn;
    error?: string;
    value?: string;
    disabled?: boolean;
    isEditable?: boolean;
    id?: string;
    editNavigationPath?: string;
    onEditClick?: () => void;
    className?: string;
}

const TOOLTIP_DURATION = 2000;
const MAX_ZIP_LENGTH = 5;

const showInvalidCharTooltip = (
    inputRef: React.MutableRefObject<HTMLInputElement | null>,
    tooltipTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    target: HTMLInputElement,
    setShowTooltip: (show: boolean) => void
) => {
    inputRef.current = target;
    setShowTooltip(true);
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    tooltipTimeoutRef.current = setTimeout(() => setShowTooltip(false), TOOLTIP_DURATION);
};

const getInputClassName = (hasError: boolean, isDisabled: boolean, extra: string): string => {
    const baseClass = "input input-bordered bg-base-100 h-9 text-sm";
    const errorClass = hasError ? "input-error" : "";
    const disabledClass = isDisabled ? "input-disabled" : "";
    return [baseClass, errorClass, disabledClass, extra].filter(Boolean).join(" ");
};

const ZipCodeField = ({
    register,
    error,
    value,
    disabled = false,
    id,
    isEditable = false,
    editNavigationPath,
    onEditClick,
    className = "",
}: ZipCodeFieldProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    const handleEditClick = () => {
        if (onEditClick) {
            onEditClick();
        } else if (editNavigationPath) {
            navigate(editNavigationPath);
            setTimeout(() => {
                document.getElementById("hero-card")?.scrollIntoView({ behavior: "auto" });
            }, 0);
        }
    };

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            const numericValue = inputValue.replace(nonDigitGlobalRegex, "");
            const hasInvalidChars = inputValue !== numericValue && inputValue.length > 0;

            if (hasInvalidChars) {
                showInvalidCharTooltip(inputRef, tooltipTimeoutRef, e.target, setShowTooltip);
            }

            if (numericValue.length > MAX_ZIP_LENGTH) return;

            e.target.value = numericValue;
            register.onChange?.(e);
        },
        [register]
    );

    const setRefs = useCallback(
        (e: HTMLInputElement | null) => {
            register.ref?.(e);
            inputRef.current = e;
        },
        [register]
    );

    const inputClassName = getInputClassName(!!error, disabled, className);

    const labelElement = (
        <label className="label h-7" htmlFor={id}>
            <span className="text-base-content">Zip Code</span>
        </label>
    );

    const labelSection = isEditable ? (
        <div className="flex justify-between items-center">
            {labelElement}
            <button onClick={handleEditClick} className="text-edit-link font-bold text-sm leading-5 hover:underline">
                Edit
            </button>
        </div>
    ) : (
        labelElement
    );

    return (
        <div className="form-control w-full">
            {labelSection}
            <input
                {...register}
                onChange={handleChange}
                ref={setRefs}
                id={id}
                type="text"
                placeholder="12345"
                className={inputClassName}
                maxLength={5}
                disabled={disabled}
                defaultValue={value}
            />
            <InputValidationTooltip message="Only numbers allowed" show={showTooltip} targetRef={inputRef} />
            {error && <p className="text-error text-xs mt-1">{error}</p>}
        </div>
    );
};

export default ZipCodeField;
